// /online-physical-therapy — the paid-search landing page for the membership offer.
//
// It lives in the funnel app on purpose, not on WordPress: the marketing site runs the
// Meta pixel and Hotjar, and a "where does it hurt?" picker there would hand condition
// signals (tapped button text) to both. Here the only tag is GA4 behind the PHI
// allowlist, utm_* are captured on the same origin, and a tapped body area goes
// straight into the questionnaire's local state (never into a URL or an event).
//
// Copy rules (growth/content/landing-questionnaire.md): no treatment or cure claims,
// "supports your care, doesn't replace it", trial and cancellation stated on the page,
// no em dashes in patient copy. The monthly price is deliberately NOT shown: the
// marketing site's own FAQ says the price is shown after the assessment, so the page
// matches it. Add it to OFFER if that changes.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { PrimaryButton, card } from '../components/ui';
import { useIsNarrow } from '../lib/useIsNarrow';
import { AreaPicker } from './AreaPicker';
import { ProviderStrip } from './ProviderStrip';

const TRIAL_DAYS = Number(import.meta.env.VITE_TRIAL_DAYS || 7);
const OFFER = [`Free for ${TRIAL_DAYS} days`, 'No card to start', 'Cancel anytime'];
const SITE = 'https://expyhealth.com';

const STEPS = [
  { title: 'Answer a few questions', body: 'Tell us where it hurts and what you want to get back to. It takes about 2 minutes.' },
  { title: 'Get your plan', body: 'Short, guided daily sessions in the Expy app that adjust as you progress.' },
  {
    title: 'Add a physical therapist if you want',
    body: 'We help you find a licensed physical therapist in your state. Video visits are booked and paid separately.',
  },
];

const INCLUDED = [
  'A plan for your body area and goal, whether it follows surgery, an injury or ongoing pain',
  'Guided exercise videos with reps and timers',
  'Progress tracking and a weekly check-in',
  'Reminders that help you keep your streak',
  'Help finding a licensed physical therapist in your state',
];

// Real patient quotes only, never invented: Jeremy S. and Martin A. are the two from the
// app's onboarding "Your program is ready" screen (design handoff), George and Suzanne
// are the site's.
const REVIEWS = [
  { quote: 'This app really helped with my knee pain. A must have for anyone who needs physical therapy.', name: 'Jeremy S.' },
  {
    quote: 'I had back pain and elbow pain. This app helped me stay on track with my recovery. I feel better now after following the exercises.',
    name: 'Martin A.',
  },
  {
    quote: "I use the app daily now. It's really helped me reduce my knee pain. I never expected to make this much progress after all these years.",
    name: 'George',
  },
  { quote: 'Being able to have a resource like this to help you make progress at home is such a great help!', name: 'Suzanne' },
];

const FAQ = [
  {
    q: "What's included in my Expy membership?",
    a: 'Daily guided exercises personalized to your recovery, progress tracking, and our concierge matching service: we help you find a licensed physical therapist in your state from our network of vetted providers.',
  },
  {
    q: 'How much does Expy cost?',
    a: `Expy is one simple monthly membership that covers your entire recovery program. Your first ${TRIAL_DAYS} days are free with no card required, so you can try the full program before you pay anything. You'll see the exact monthly price before you subscribe, and you can cancel anytime. Video visits with a physical therapist are booked and billed separately.`,
  },
  {
    q: 'Can I book a session with a physical therapist?',
    a: "Yes. Once you're matched, you can message your physical therapist in the Expy app and book a video session with them. Visits are billed separately, per visit.",
  },
  {
    q: 'Does insurance cover my Expy membership?',
    a: "No. The membership is a self-pay subscription and isn't billed to insurance. A session with a physical therapist is booked separately and may be covered by your insurance.",
  },
  { q: 'What do I need to get started?', a: 'Just a smartphone. No referral needed to start your program.' },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Cancel anytime from your account settings or your App Store or Google Play subscription settings.',
  },
  {
    q: 'Is Expy a replacement for seeing my doctor or physical therapist in person?',
    a: 'No. Expy is designed to complement your care, not replace medical advice from your doctor or in-person provider.',
  },
];

// Section wrapper: centred column with the page's side gutters.
function Section({ children, tint, narrow, style }: { children: ReactNode; tint?: boolean; narrow: boolean; style?: CSSProperties }) {
  return (
    <section style={{ background: tint ? 'var(--surface)' : '#fff', ...style }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: narrow ? '48px 20px' : '72px 32px' }}>{children}</div>
    </section>
  );
}

function H2({ children, narrow }: { children: ReactNode; narrow: boolean }) {
  return (
    <h2 className="pretty" style={{ fontSize: narrow ? 26 : 32, fontWeight: 800, lineHeight: 1.2, margin: '0 0 28px', textAlign: 'center' }}>
      {children}
    </h2>
  );
}

function Check() {
  return (
    <svg width={20} height={20} viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden>
      <circle cx={10} cy={10} r={10} fill="var(--teal-tint)" />
      <path d="M5.8 10.2l2.8 2.8 5.6-5.8" stroke="var(--teal-deep)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #E5ECEE' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, padding: '18px 0', textAlign: 'left' }}
      >
        <span style={{ fontSize: 16, fontWeight: 800 }}>{q}</span>
        <svg width={14} height={14} viewBox="0 0 14 14" fill="none" aria-hidden style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s ease' }}>
          <path d="M3 5l4 4 4-4" stroke="var(--teal-deep)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <p style={{ margin: '0 0 18px', fontSize: 15, lineHeight: 1.6, color: 'var(--slate-soft)' }}>{a}</p>}
    </div>
  );
}

export function OnlinePT({ onStart }: { onStart: (area?: string) => void }) {
  const narrow = useIsNarrow();
  const heroRef = useRef<HTMLDivElement>(null);
  // The pinned phone CTA appears once the hero picker has scrolled away, so it never
  // doubles up with the tiles on first view.
  const [pastHero, setPastHero] = useState(false);
  useEffect(() => {
    const el = heroRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setPastHero(!entry.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div style={{ background: '#fff', paddingBottom: narrow && pastHero ? 88 : 0 }}>
      {/* Header: logo only. No navigation on a paid landing page. */}
      <header style={{ maxWidth: 1080, margin: '0 auto', padding: narrow ? '14px 20px' : '18px 32px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <img src="/expy-man.png" alt="" width={26} height={26} style={{ display: 'block' }} />
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>Expy Health</span>
      </header>

      {/* Hero */}
      <div style={{ background: 'linear-gradient(180deg, var(--teal-tint) 0%, #fff 100%)' }}>
        <div
          ref={heroRef}
          style={{
            maxWidth: 1080,
            margin: '0 auto',
            padding: narrow ? '28px 20px 40px' : '56px 32px 72px',
            display: 'grid',
            gridTemplateColumns: narrow ? '1fr' : '1.1fr 1fr',
            gap: narrow ? 28 : 48,
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--teal-deep)', marginBottom: 12 }}>
              Physical therapy at home
            </div>
            <h1 className="pretty" style={{ fontSize: narrow ? 32 : 46, fontWeight: 800, lineHeight: 1.12, margin: 0 }}>
              A recovery plan built around you
            </h1>
            <p className="pretty" style={{ fontSize: narrow ? 17 : 19, lineHeight: 1.55, color: 'var(--slate-soft)', margin: '16px 0 22px', maxWidth: 520 }}>
              Guided daily exercises in the Expy app, personalized to your pain and your goals, with licensed physical therapists behind every program.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {OFFER.map((o) => (
                <li key={o} style={{ background: '#fff', borderRadius: 999, padding: '8px 14px', fontSize: 14, fontWeight: 800, color: 'var(--teal-deep)', boxShadow: 'var(--card-shadow)' }}>
                  {o}
                </li>
              ))}
            </ul>
          </div>
          <AreaPicker onPick={(area) => onStart(area)} narrow={narrow} />
        </div>
      </div>

      {/* How it works */}
      <Section narrow={narrow}>
        <H2 narrow={narrow}>How it works</H2>
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(3, 1fr)', gap: 16 }}>
          {STEPS.map((s, i) => (
            <div key={s.title} style={{ ...card, padding: 24 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--teal)', color: '#fff', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                {i + 1}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>{s.title}</div>
              <div style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--slate-soft)' }}>{s.body}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* What's included */}
      <Section narrow={narrow} tint>
        <H2 narrow={narrow}>What's in your membership</H2>
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 auto', maxWidth: 620, display: 'grid', gap: 14 }}>
          {INCLUDED.map((item) => (
            <li key={item} style={{ display: 'flex', gap: 12, fontSize: 16, lineHeight: 1.5, fontWeight: 600 }}>
              <Check />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Real bookable clinicians (US PTs + UK physios). No links: one CTA, fill the funnel. */}
      <ProviderStrip narrow={narrow} />

      {/* Reviews: initials, never stock faces. */}
      <Section narrow={narrow}>
        <H2 narrow={narrow}>What patients say</H2>
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 16, maxWidth: 860, margin: '0 auto' }}>
          {REVIEWS.map((r) => (
            <figure key={r.name} style={{ ...card, padding: 24, margin: 0 }}>
              <blockquote style={{ margin: 0, fontSize: 16, lineHeight: 1.6 }}>"{r.quote}"</blockquote>
              <figcaption style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
                <span aria-hidden style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--teal)', color: '#fff', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {r.name[0]}
                </span>
                <span style={{ fontSize: 14, fontWeight: 800 }}>
                  {r.name}
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--teal-deep)' }}>Patient</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section narrow={narrow} tint>
        <H2 narrow={narrow}>Questions</H2>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          {FAQ.map((f) => (
            <FaqItem key={f.q} q={f.q} a={f.a} />
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section narrow={narrow}>
        <div style={{ textAlign: 'center' }}>
          <H2 narrow={narrow}>Start your plan today</H2>
          <p style={{ fontSize: 16, color: 'var(--slate-soft)', margin: '-12px 0 24px' }}>{OFFER.join(' · ')}</p>
          <PrimaryButton onClick={() => onStart()}>Build my free plan</PrimaryButton>
        </div>
      </Section>

      <footer style={{ borderTop: '1px solid #E5ECEE' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', padding: narrow ? '20px 20px 28px' : '24px 32px', display: 'flex', flexWrap: 'wrap', gap: 18, fontSize: 13, fontWeight: 600, color: 'var(--slate)' }}>
          <span>© {new Date().getFullYear()} Expy Health</span>
          <a href={`${SITE}/privacy-policy/`}>Privacy</a>
          <a href={`${SITE}/terms-of-service/`}>Terms</a>
          <a href={`${SITE}/support/`}>Support</a>
        </div>
      </footer>

      {/* Pinned phone CTA (the mobile stand-in for a popup: always one tap from starting). */}
      {narrow && pastHero && (
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, background: '#fff', boxShadow: '0 -4px 24px rgba(0,0,0,.08)', padding: '12px 20px calc(12px + env(safe-area-inset-bottom))', zIndex: 10 }}>
          <PrimaryButton full onClick={() => onStart()}>
            Get started for free today
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}
